import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Fixed action and status columns keep every row's Review button and status
 * pill in the same place, whatever the title length and whether the row has a
 * button. Pills sit at the far right; a longer reviewer conclusion wraps inside
 * its pill. On narrow screens the columns shrink to fit instead.
 */
const COLUMNS_WITH_ACTION =
  "grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_7.5rem_12.5rem]";
export const COLUMNS_WITHOUT_ACTION =
  "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_12.5rem]";

export function SourceRowHeader({
  title,
  badge,
  summary,
  label,
  onClick,
  disclosure,
  action,
}: {
  title: string;
  badge: ReactNode;
  summary: ReactNode;
  label: string;
  onClick: () => void;
  /** Rows nested inside a check dialog expand in place; every other row
   * opens its check dialog. */
  disclosure?: { expanded: boolean; controls: string };
  /** Rendered only in a review workspace; its column is reserved even when
   * the action renders nothing, so buttons and pills stay aligned. */
  action?: ReactNode;
}) {
  const hasAction = action !== undefined;
  const expanded = disclosure?.expanded ?? false;
  return (
    // The whole row is the button. The action sits in the middle column on
    // top of it, so clicking the action does not also open the row.
    <div
      data-source-row
      className={cn(
        "grid items-center",
        hasAction ? COLUMNS_WITH_ACTION : COLUMNS_WITHOUT_ACTION,
        expanded && "border-b bg-slate-50",
      )}
    >
      <button
        type="button"
        className="col-span-full col-start-1 row-start-1 grid cursor-pointer grid-cols-subgrid items-center p-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-4"
        data-row-trigger
        aria-label={label}
        aria-haspopup={disclosure ? undefined : "dialog"}
        aria-expanded={disclosure?.expanded}
        aria-controls={disclosure?.controls}
        onClick={onClick}
      >
        <div className="min-w-0 space-y-1.5">
          <h4 className="text-base font-semibold">{title}</h4>
          {summary}
        </div>
        <div className="col-start-[-2] justify-self-end pl-3">{badge}</div>
      </button>
      {hasAction && (
        <div className="relative col-start-2 row-start-1 justify-self-end pl-3">
          {action}
        </div>
      )}
    </div>
  );
}
